import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs, updateDoc, doc, getDoc, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { ShoppingCart, Search, Calendar, DollarSign, User, MapPin, Clock, X, CheckCircle, XCircle, Eye } from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner';

export default function OrdersView() {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<any[]>([]);
  const [serviceProviders, setServiceProviders] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Extract basic provider info from selected order (no dependencies on serviceProviders)
  const providerName = selectedOrder?.providerName || selectedOrder?.serviceProviderName;
  const providerPhone = selectedOrder?.providerPhone || selectedOrder?.serviceProviderPhone;
  const providerEmail = selectedOrder?.providerEmail || selectedOrder?.serviceProviderEmail;
  const providerId = selectedOrder?.acceptedBy || selectedOrder?.serviceProviderId || selectedOrder?.providerId;
  
  // Get provider service data (only when needed, not in useMemo to avoid loop)
  const getProviderServiceData = () => {
    if (!selectedOrder || !providerId || !serviceProviders[providerId]) return null;
    
    const provider = serviceProviders[providerId];
    if (provider.acceptedServices && Array.isArray(provider.acceptedServices)) {
      return provider.acceptedServices.find(
        (service: any) => service.orderId === selectedOrder.id
      );
    }
    return null;
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, 'orders'));
      const ordersData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        };
      });

      // Sort by created date (newest first)
      ordersData.sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA;
      });

      setOrders(ordersData);

      // Fetch service provider details for all orders with provider IDs
      await loadServiceProviders(ordersData);
    } catch (error) {
      console.error('Error loading orders:', error);
      alert('Failed to load orders: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const loadServiceProviders = async (ordersData: any[]) => {
    try {
      const providerIds = new Set<string>();
      ordersData.forEach(order => {
        // Check all possible field names for provider ID
        const providerId = order.acceptedBy || order.serviceProviderId || order.providerId;
        if (providerId) {
          providerIds.add(providerId);
        }
      });

      if (providerIds.size === 0) {
        return;
      }

      const providersMap: Record<string, any> = {};

      // Batch fetch providers using 'in' query (max 10 IDs per query)
      const providerIdsArray = Array.from(providerIds);
      
      // Process in chunks of 10 (Firestore 'in' limit)
      for (let i = 0; i < providerIdsArray.length; i += 10) {
        const chunk = providerIdsArray.slice(i, i + 10);
        try {
          const providersQuery = query(
            collection(db, 'serviceProviders'),
            where('__name__', 'in', chunk)
          );
          
          const snapshot = await getDocs(providersQuery);
          snapshot.docs.forEach(doc => {
            providersMap[doc.id] = {
              id: doc.id,
              ...doc.data()
            };
          });
          console.log(`Loaded ${snapshot.size} providers from chunk`);
        } catch (err) {
          console.error(`Error fetching provider chunk:`, err);
        }
      }

      setServiceProviders(providersMap);
      console.log('Service providers loaded:', Object.keys(providersMap).length);
    } catch (error) {
      console.error('Error loading service providers:', error);
    }
  };

  const updateOrderStatus = async (orderId: string, status: 'placed' | 'pending' | 'confirmed' | 'active' | 'partial-completed' | 'completed' | 'cancelled' | 'cancelledByUser' | 'cancelledByProvider') => {
    try {
      setActionLoading(true);
      const timestamp = new Date().toISOString();
      const updateData: any = {
        status,
        updatedAt: timestamp
      };
      
      // Add status-specific timestamps
      if (status === 'confirmed') {
        updateData.confirmedAt = timestamp;
      } else if (status === 'active') {
        updateData.activeAt = timestamp;
      } else if (status === 'partial-completed') {
        updateData.partialCompletedAt = timestamp;
      } else if (status === 'completed') {
        updateData.completedAt = timestamp;
      } else if (status === 'placed') {
        updateData.placedAt = timestamp;
      } else if (status === 'pending') {
        updateData.pendingAt = timestamp;
      } else if (status === 'cancelled' || status === 'cancelledByUser' || status === 'cancelledByProvider') {
        updateData.cancelledAt = timestamp;
        if (status === 'cancelledByUser') {
          updateData.cancelledBy = 'user';
        } else if (status === 'cancelledByProvider') {
          updateData.cancelledBy = 'provider';
        }
      }
      
      await updateDoc(doc(db, 'orders', orderId), updateData);
      await loadOrders();
      alert(`Order status updated to ${status}`);
      // Reload the page data to reflect the change
      window.location.reload();
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order status: ' + (error as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  // Update individual item status within an order
  const updateItemStatus = async (
    orderId: string, 
    itemIndex: number, 
    status: 'placed' | 'confirmed' | 'active' | 'completed' | 'cancelled',
    providerId?: string,
    providerName?: string,
    providerPhone?: string
  ) => {
    try {
      setActionLoading(true);
      const timestamp = new Date().toISOString();
      
      // Get current order
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDoc(orderRef);
      
      if (!orderSnap.exists()) {
        throw new Error('Order not found');
      }
      
      const orderData = orderSnap.data();
      const items = [...(orderData.items || [])];
      
      if (itemIndex >= items.length) {
        throw new Error('Item not found');
      }
      
      // Update item status
      items[itemIndex] = {
        ...items[itemIndex],
        status,
        ...(providerId && { providerId }),
        ...(providerName && { providerName }),
        ...(providerPhone && { providerPhone }),
      };
      
      // Add timestamp
      if (status === 'confirmed') {
        items[itemIndex].confirmedAt = timestamp;
      } else if (status === 'active') {
        items[itemIndex].startedAt = timestamp;
      } else if (status === 'completed') {
        items[itemIndex].completedAt = timestamp;
      } else if (status === 'cancelled') {
        items[itemIndex].cancelledAt = timestamp;
      }
      
      // Determine overall order status based on all items
      const allCompleted = items.every((item: any) => item.status === 'completed');
      const allCancelled = items.every((item: any) => item.status === 'cancelled');
      const someCompleted = items.some((item: any) => item.status === 'completed');
      const someActive = items.some((item: any) => item.status === 'active');
      const someConfirmed = items.some((item: any) => item.status === 'confirmed');
      
      let overallStatus = orderData.status;
      if (allCompleted) {
        overallStatus = 'completed';
      } else if (allCancelled) {
        overallStatus = 'cancelled';
      } else if (someActive) {
        overallStatus = 'active';
      } else if (someConfirmed) {
        overallStatus = 'confirmed';
      } else if (someCompleted) {
        overallStatus = 'partial-completed';
      }
      
      // Update order
      await updateDoc(orderRef, {
        items,
        status: overallStatus,
        updatedAt: timestamp
      });
      
      await loadOrders();
      alert(`Item status updated to ${status}`);
      window.location.reload();
    } catch (error) {
      console.error('Error updating item status:', error);
      alert('Failed to update item status: ' + (error as Error).message);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    // Normalize 'in-progress' to 'active'
    const normalizedStatus = status === 'in-progress' ? 'active' : status;
    
    const styles = {
      placed: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-indigo-100 text-indigo-800',
      active: 'bg-purple-100 text-purple-800',
      'partial-completed': 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      cancelledByUser: 'bg-orange-100 text-orange-800',
      cancelledByProvider: 'bg-pink-100 text-pink-800'
    };
    
    // Better display names
    const displayNames: Record<string, string> = {
      placed: 'Placed',
      pending: 'Pending',
      confirmed: 'Confirmed',
      active: 'In Progress',
      'partial-completed': 'Partial Completed',
      completed: 'Completed',
      cancelled: 'Cancelled',
      cancelledByUser: 'Cancelled by User',
      cancelledByProvider: 'Cancelled by Provider'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[normalizedStatus as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {displayNames[normalizedStatus] || normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)}
      </span>
    );
  };

  const filteredOrders = orders.filter((order: any) => {
    // Default status to 'placed' if not set
    const orderStatus = order.status || 'placed';
    
    if (statusFilter !== 'all' && orderStatus !== statusFilter) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    
    // Get customer info from various possible locations
    const customerName = order.customerName || order.userName || order.address?.fullName || '';
    const customerEmail = order.customerEmail || order.userEmail || order.address?.email || '';
    const serviceName = order.serviceName || order.items?.[0]?.name || '';
    
    return (
      order.id?.toLowerCase().includes(search) ||
      serviceName.toLowerCase().includes(search) ||
      customerName.toLowerCase().includes(search) ||
      customerEmail.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Orders Management</h1>
        <p className="text-gray-600 mt-1">View and manage customer orders</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search orders..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setStatusFilter('placed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'placed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Placed
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'pending'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setStatusFilter('confirmed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'confirmed'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Confirmed
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                statusFilter === 'completed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Completed
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="p-12 text-center">
            <ShoppingCart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No orders have been placed yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handler</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order: any) => (
                  <motion.tr
                    key={order.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div 
                          className="text-sm font-medium text-gray-900 cursor-pointer hover:text-primary-600 transition-colors"
                          title={`Full Order ID: ${order.id} (Click to copy)`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(order.id || '');
                            alert('Order ID copied to clipboard!');
                          }}
                        >
                          #{order.id?.slice(-8) || 'N/A'}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(order.id || '');
                            alert('Full Order ID copied: ' + order.id);
                          }}
                          className="text-gray-400 hover:text-primary-600 transition-colors"
                          title="Copy full Order ID"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {order.customerName || order.userName || order.address?.fullName || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.customerEmail || order.userEmail || order.address?.email || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900">
                          {order.serviceName || order.items?.[0]?.name || 'N/A'}
                        </div>
                        {(order.serviceCategory || order.category || order.items?.[0]?.category) && (
                          <div className="text-xs text-gray-500">
                            {order.serviceCategory || order.category || order.items?.[0]?.category}
                          </div>
                        )}
                        {order.items?.[0] && (
                          <div className="flex items-center gap-2 text-xs text-gray-600">
                            {order.items[0].quantity > 1 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                ×{order.items[0].quantity}
                              </span>
                            )}
                            {order.items[0].addons && order.items[0].addons.length > 0 && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                                +{order.items[0].addons.length} addon{order.items[0].addons.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        )}
                        {order.items && order.items.length > 1 && (
                          <div className="text-xs text-indigo-600 font-medium">
                            +{order.items.length - 1} more item{order.items.length > 2 ? 's' : ''}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {order.createdAt ? new Date(order.createdAt).toLocaleTimeString() : ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {order.scheduling && order.scheduling.date && order.scheduling.time ? (
                        <>
                          <div className="text-sm text-gray-900 font-medium">
                            📅 {order.scheduling.date}
                          </div>
                          <div className="text-sm text-gray-500">
                            🕐 {order.scheduling.time}
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-gray-400 italic">Not scheduled</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ₹{order.totalAmount || order.payment?.total || 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(order.status || 'placed')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {(() => {
                          // Check for provider information directly in the order
                          const providerName = order.providerName || order.serviceProviderName;
                          const providerPhone = order.providerPhone || order.serviceProviderPhone;
                          const providerEmail = order.providerEmail || order.serviceProviderEmail;
                          const providerId = order.serviceProviderId || order.providerId;
                          
                          // If we have provider name or phone, show it
                          if (providerName || providerPhone) {
                            return (
                              <div className="space-y-1">
                                {providerName && (
                                  <div className="font-medium text-gray-900">
                                    {providerName}
                                  </div>
                                )}
                                {providerPhone && (
                                  <div className="text-xs text-gray-500">
                                    📞 {providerPhone}
                                  </div>
                                )}
                                {providerEmail && (
                                  <div className="text-xs text-gray-500">
                                    ✉️ {providerEmail}
                                  </div>
                                )}
                                {providerId && (
                                  <div 
                                    className="text-xs bg-blue-50 text-blue-700 font-medium px-2 py-1 rounded border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                                    title={`Full Provider ID: ${providerId} (Click to copy)`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(providerId);
                                      alert('Provider ID copied: ' + providerId);
                                    }}
                                  >
                                    🆔 {providerId.length > 12 ? providerId.slice(0, 12) + '...' : providerId}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Fallback: check if we fetched provider details
                          if (providerId && serviceProviders[providerId]) {
                            const provider = serviceProviders[providerId];
                            return (
                              <div className="space-y-1">
                                <div className="font-medium text-gray-900">
                                  {provider.name || provider.fullName || 'Unknown Provider'}
                                </div>
                                <div className="text-xs text-gray-500">
                                  📞 {provider.phone || provider.phoneNumber || 'N/A'}
                                </div>
                                {provider.email && (
                                  <div className="text-xs text-gray-500">
                                    ✉️ {provider.email}
                                  </div>
                                )}
                                <div className="text-xs text-blue-600 font-medium">
                                  ID: {providerId.slice(-8)}
                                </div>
                              </div>
                            );
                          }
                          
                          // No provider assigned
                          return <span className="text-gray-400 italic">Not assigned</span>;
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => {
                            console.log('View button clicked for order:', order);
                            setSelectedOrder(order);
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <AnimatePresence>
        {selectedOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedOrder(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Details</h2>
                    <div className="flex items-center gap-3">
                      <span className="text-gray-600">Order ID:</span>
                      <div className="flex items-center gap-2">
                        <span 
                          className="font-mono bg-gray-100 px-3 py-1 rounded text-gray-900 border border-gray-200 text-sm cursor-pointer hover:bg-gray-200 transition-colors"
                          title="Click to copy full Order ID"
                          onClick={() => {
                            navigator.clipboard.writeText(selectedOrder.id || '');
                            alert('Full Order ID copied: ' + selectedOrder.id);
                          }}
                        >
                          {selectedOrder.id || 'N/A'}
                        </span>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(selectedOrder.id || '');
                            alert('Full Order ID copied to clipboard!');
                          }}
                          className="text-gray-400 hover:text-primary-600 transition-colors p-1"
                          title="Copy Order ID"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedOrder(null)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Status */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Current Status</h3>
                    {getStatusBadge(selectedOrder.status || 'placed')}
                  </div>

                  {/* Customer Information */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <User className="h-5 w-5 mr-2" />
                      Customer Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Name:</span> {selectedOrder.customerName || selectedOrder.userName || selectedOrder.address?.fullName || 'N/A'}</p>
                      <p><span className="font-medium">Email:</span> {selectedOrder.customerEmail || selectedOrder.userEmail || selectedOrder.address?.email || 'N/A'}</p>
                      <p><span className="font-medium">Phone:</span> {selectedOrder.customerPhone || selectedOrder.userPhone || selectedOrder.address?.phone || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Service Details - Enhanced */}
                  <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <ShoppingCart className="h-5 w-5 mr-2" />
                      Service Details
                    </h3>
                    
                    {selectedOrder.items && selectedOrder.items.length > 0 ? (
                      <div className="space-y-4">
                        {selectedOrder.items.map((item: any, index: number) => (
                          <div key={item.id || index} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                            {/* Service Header */}
                            <div className="flex items-start justify-between mb-3 pb-3 border-b border-gray-200">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-gray-900 text-base">{item.name}</h4>
                                  {/* Item Status Badge */}
                                  {item.status && (
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      item.status === 'completed' ? 'bg-green-100 text-green-800' :
                                      item.status === 'active' ? 'bg-purple-100 text-purple-800' :
                                      item.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                                      item.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {item.status === 'completed' ? 'Completed' :
                                       item.status === 'active' ? 'In Progress' :
                                       item.status === 'confirmed' ? 'Confirmed' :
                                       item.status === 'cancelled' ? 'Cancelled' :
                                       'Placed'}
                                    </span>
                                  )}
                                </div>
                                {(item.category || selectedOrder.serviceCategory || selectedOrder.category) && (
                                  <p className="text-sm text-gray-600 mt-1">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {item.category || selectedOrder.serviceCategory || selectedOrder.category}
                                    </span>
                                  </p>
                                )}
                                {/* Scheduling Info */}
                                {item.scheduling && (
                                  <p className="text-xs text-gray-600 mt-2">
                                    📅 {item.scheduling.date} • {item.scheduling.time}
                                  </p>
                                )}
                                {/* Provider Info */}
                                {item.providerName && (
                                  <p className="text-xs text-gray-600 mt-1">
                                    👤 Provider: {item.providerName} {item.providerPhone && `(${item.providerPhone})`}
                                  </p>
                                )}
                              </div>
                              {item.image && (
                                <img 
                                  src={item.image} 
                                  alt={item.name} 
                                  className="w-16 h-16 object-cover rounded-lg ml-3"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                />
                              )}
                            </div>
                            
                            {/* Pricing Details */}
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              <div className="bg-green-50 rounded-lg p-3">
                                <p className="text-xs text-gray-600 mb-1">Base Price</p>
                                <p className="text-lg font-bold text-green-700">₹{item.price?.toLocaleString() || 0}</p>
                                {item.originalPrice && item.originalPrice > item.price && (
                                  <p className="text-xs text-gray-500 line-through">₹{item.originalPrice.toLocaleString()}</p>
                                )}
                              </div>
                              
                              <div className="bg-blue-50 rounded-lg p-3">
                                <p className="text-xs text-gray-600 mb-1">Quantity</p>
                                <p className="text-lg font-bold text-blue-700">×{item.quantity || 1}</p>
                                <p className="text-xs text-gray-500">People/Units</p>
                              </div>
                            </div>
                            
                            {/* Duration & Provider */}
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              {item.duration && (
                                <div className="flex items-center gap-2 text-sm">
                                  <Clock className="h-4 w-4 text-gray-500" />
                                  <span className="text-gray-700">{item.duration}</span>
                                </div>
                              )}
                              {item.provider && (
                                <div className="flex items-center gap-2 text-sm">
                                  <User className="h-4 w-4 text-gray-500" />
                                  <span className="text-gray-700">{item.provider}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Addons/Extras */}
                            {item.addons && item.addons.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Selected Add-ons</p>
                                <div className="space-y-2">
                                  {item.addons.map((addon: any, addonIndex: number) => (
                                    <div key={addon.id || addonIndex} className="flex items-center justify-between bg-purple-50 rounded-lg p-2">
                                      <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                                        <span className="text-sm text-gray-800">{addon.name}</span>
                                        {addon.duration && (
                                          <span className="text-xs text-gray-500">({addon.duration})</span>
                                        )}
                                      </div>
                                      <span className="text-sm font-semibold text-purple-700">+₹{addon.price?.toLocaleString() || 0}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Item Total Calculation */}
                            <div className="mt-3 pt-3 border-t-2 border-gray-300 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3">
                              <div className="space-y-1.5">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Base Amount:</span>
                                  <span className="text-gray-900">₹{item.price?.toLocaleString() || 0} × {item.quantity || 1}</span>
                                </div>
                                
                                {item.addons && item.addons.length > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Add-ons Total:</span>
                                    <span className="text-gray-900">
                                      +₹{(item.addons.reduce((sum: number, addon: any) => sum + (addon.price || 0), 0) * (item.quantity || 1)).toLocaleString()}
                                    </span>
                                  </div>
                                )}
                                
                                <div className="flex justify-between pt-2 border-t border-indigo-300">
                                  <span className="font-bold text-gray-900">Item Total:</span>
                                  <span className="font-bold text-indigo-700 text-lg">
                                    ₹{(
                                      (item.price || 0) * (item.quantity || 1) + 
                                      (item.addons?.reduce((sum: number, addon: any) => sum + (addon.price || 0), 0) || 0) * (item.quantity || 1)
                                    ).toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Item Status Management Actions */}
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-xs font-semibold text-gray-700 mb-2">Item Actions:</p>
                              <div className="flex flex-wrap gap-2">
                                {(!item.status || item.status === 'placed') && (
                                  <button
                                    onClick={() => {
                                      const provider = prompt('Enter Provider Name:');
                                      const phone = prompt('Enter Provider Phone:');
                                      if (provider) {
                                        updateItemStatus(selectedOrder.id, index, 'confirmed', undefined, provider, phone || undefined);
                                      }
                                    }}
                                    disabled={actionLoading}
                                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 disabled:opacity-50"
                                  >
                                    Confirm & Assign Provider
                                  </button>
                                )}
                                {item.status === 'confirmed' && (
                                  <button
                                    onClick={() => updateItemStatus(selectedOrder.id, index, 'active')}
                                    disabled={actionLoading}
                                    className="px-3 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600 disabled:opacity-50"
                                  >
                                    Start Service
                                  </button>
                                )}
                                {item.status === 'active' && (
                                  <button
                                    onClick={() => updateItemStatus(selectedOrder.id, index, 'completed')}
                                    disabled={actionLoading}
                                    className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
                                  >
                                    Mark Complete
                                  </button>
                                )}
                                {item.status !== 'completed' && item.status !== 'cancelled' && (
                                  <button
                                    onClick={() => {
                                      if (confirm('Are you sure you want to cancel this service item?')) {
                                        updateItemStatus(selectedOrder.id, index, 'cancelled');
                                      }
                                    }}
                                    disabled={actionLoading}
                                    className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
                                  >
                                    Cancel Item
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Grand Total if multiple items */}
                        {selectedOrder.items.length > 1 && (
                          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border-2 border-green-300">
                            <div className="flex justify-between items-center">
                              <span className="text-lg font-bold text-gray-900">Grand Total ({selectedOrder.items.length} items):</span>
                              <span className="text-2xl font-bold text-green-700">
                                ₹{selectedOrder.totalAmount || selectedOrder.payment?.total || 0}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Fallback for old orders without items array
                      <div className="space-y-2 text-sm">
                        {selectedOrder.serviceName && (
                          <p><span className="font-medium">Service:</span> {selectedOrder.serviceName}</p>
                        )}
                        {(selectedOrder.serviceCategory || selectedOrder.category) && (
                          <p><span className="font-medium">Category:</span> {selectedOrder.serviceCategory || selectedOrder.category}</p>
                        )}
                        <p><span className="font-medium">Amount:</span> ₹{selectedOrder.totalAmount || selectedOrder.payment?.total || 0}</p>
                        {selectedOrder.description && (
                          <p><span className="font-medium">Description:</span> {selectedOrder.description}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Payment Status & Additional Charges (for active/completed orders) */}
                  {(selectedOrder.status === 'active' || selectedOrder.status === 'completed') && (
                    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-lg p-4 border-2 border-yellow-200">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                        <DollarSign className="h-5 w-5 mr-2 text-yellow-600" />
                        Payment & Additional Charges
                      </h3>
                      <div className="space-y-3">
                        {/* Original Amount */}
                        <div className="flex justify-between items-center pb-2 border-b border-yellow-200">
                          <span className="text-sm text-gray-600">Original Amount:</span>
                          <span className="text-base font-semibold text-gray-900">
                            ₹{selectedOrder.originalAmount || selectedOrder.totalAmount || 0}
                          </span>
                        </div>
                        
                        {/* Additional Charges */}
                        {selectedOrder.additionalCharges && selectedOrder.additionalCharges.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-gray-700">Additional Charges:</p>
                            {selectedOrder.additionalCharges.map((charge: any, index: number) => (
                              <div key={index} className="flex justify-between items-center text-sm pl-4">
                                <span className="text-gray-600">• {charge.description || charge.name}</span>
                                <span className="font-medium text-gray-900">+₹{charge.amount || 0}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* New Total */}
                        {selectedOrder.finalAmount && selectedOrder.finalAmount !== selectedOrder.totalAmount && (
                          <div className="flex justify-between items-center pt-2 border-t-2 border-yellow-300">
                            <span className="text-base font-bold text-gray-900">Final Amount:</span>
                            <span className="text-xl font-bold text-yellow-700">
                              ₹{selectedOrder.finalAmount}
                            </span>
                          </div>
                        )}
                        
                        {/* Payment Status */}
                        <div className="flex justify-between items-center bg-white rounded-lg p-3 mt-3">
                          <span className="text-sm font-medium text-gray-700">Payment Status:</span>
                          {selectedOrder.paymentStatus === 'paid' || selectedOrder.payment?.status === 'paid' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Paid
                            </span>
                          ) : selectedOrder.paymentStatus === 'pending' || selectedOrder.payment?.status === 'pending' ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                              <Clock className="h-4 w-4 mr-1" />
                              Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                              <XCircle className="h-4 w-4 mr-1" />
                              Unpaid
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Service Provider Information */}
                  {(() => {
                    // If we have any provider information, show it
                    if (providerName || providerPhone || providerEmail || providerId) {
                      return (
                        <div className="bg-indigo-50 rounded-lg p-4 border-2 border-indigo-200">
                          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                            <User className="h-5 w-5 mr-2 text-indigo-600" />
                            Service Provider Assigned
                          </h3>
                          <div className="space-y-2.5 text-sm">
                            {providerName && (
                              <p className="text-base"><span className="font-medium">Name:</span> {providerName}</p>
                            )}
                            {providerPhone && (
                              <p className="text-base"><span className="font-medium">Phone:</span> 📞 {providerPhone}</p>
                            )}
                            
                            {/* Provider ID - Simple display like name and phone */}
                            {providerId && (
                              <p className="text-base">
                                <span className="font-medium">ID:</span>{' '}
                                <span className="font-mono text-indigo-700 font-semibold">
                                  {providerId}
                                </span>
                              </p>
                            )}
                            
                            {providerEmail && (
                              <p><span className="font-medium">Email:</span> ✉️ {providerEmail}</p>
                            )}
                            
                            {/* Additional provider details - only show if exist */}
                            {providerId && serviceProviders[providerId] && (
                              <>
                                {serviceProviders[providerId].specialization && (
                                  <p className="text-sm mt-2">
                                    <span className="font-medium">Specialization:</span>{' '}
                                    {serviceProviders[providerId].specialization}
                                  </p>
                                )}
                                {serviceProviders[providerId].rating && serviceProviders[providerId].rating > 0 && (
                                  <p className="text-sm mt-2">
                                    <span className="font-medium">Rating:</span> ⭐{' '}
                                    {serviceProviders[providerId].rating.toFixed(1)}{' '}
                                    ({serviceProviders[providerId].totalReviews || 0} reviews)
                                  </p>
                                )}
                                {serviceProviders[providerId].completedJobs && serviceProviders[providerId].completedJobs > 0 && (
                                  <p className="text-sm mt-2">
                                    <span className="font-medium">Completed Jobs:</span>{' '}
                                    {serviceProviders[providerId].completedJobs}
                                  </p>
                                )}
                              </>
                            )}
                            
                            {/* Show status indicator based on order status */}
                            {selectedOrder.status === 'confirmed' && (
                              <div className="mt-3 pt-3 border-t border-indigo-300">
                                <p className="text-indigo-700 font-medium flex items-center">
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Provider has confirmed this service
                                </p>
                              </div>
                            )}
                            {selectedOrder.status === 'completed' && (
                              <div className="mt-3 pt-3 border-t border-green-300 bg-green-50 -mx-4 -mb-4 p-4 rounded-b-lg">
                                <p className="text-green-700 font-medium flex items-center">
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Service completed by this provider
                                </p>
                              </div>
                            )}
                            {(selectedOrder.status === 'pending' || selectedOrder.status === 'placed') && providerId && (
                              <div className="mt-3 pt-3 border-t border-indigo-300">
                                <p className="text-amber-600 font-medium flex items-center">
                                  <Clock className="h-4 w-4 mr-2" />
                                  Awaiting provider confirmation
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
                        <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                          <User className="h-5 w-5 mr-2 text-yellow-600" />
                          Service Provider
                        </h3>
                        <p className="text-sm text-gray-600">
                          ⚠️ No service provider has been assigned to this order yet.
                        </p>
                      </div>
                    );
                  })()}

                  {/* Booking Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <Calendar className="h-4 w-4 mr-2" />
                        Scheduled Date & Time
                      </h3>
                      <div className="text-sm space-y-1">
                        {selectedOrder.scheduling ? (
                          <>
                            <p className="font-medium text-gray-900">
                              📅 {selectedOrder.scheduling.date || 'Date not set'}
                            </p>
                            <p className="font-medium text-gray-900">
                              🕐 {selectedOrder.scheduling.time || 'Time not set'}
                            </p>
                          </>
                        ) : (
                          <p className="text-gray-600 italic">Not scheduled yet</p>
                        )}
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <DollarSign className="h-4 w-4 mr-2" />
                        Total Amount
                      </h3>
                      <p className="text-lg font-bold text-primary-600">
                        ₹{selectedOrder.totalAmount || selectedOrder.payment?.total || 0}
                      </p>
                    </div>
                  </div>

                  {/* Address */}
                  {(selectedOrder.address || selectedOrder.serviceAddress) && (
                    <div className="bg-orange-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2 flex items-center">
                        <MapPin className="h-5 w-5 mr-2" />
                        Service Address
                      </h3>
                      <div className="text-sm">
                        {typeof selectedOrder.address === 'object' ? (
                          <div className="space-y-1">
                            {selectedOrder.address.houseNumber && <p>{selectedOrder.address.houseNumber}</p>}
                            {selectedOrder.address.streetAddress && <p>{selectedOrder.address.streetAddress}</p>}
                            {selectedOrder.address.landmark && <p>Landmark: {selectedOrder.address.landmark}</p>}
                            {selectedOrder.address.city && selectedOrder.address.state && (
                              <p>{selectedOrder.address.city}, {selectedOrder.address.state}</p>
                            )}
                            {selectedOrder.address.pincode && <p>PIN: {selectedOrder.address.pincode}</p>}
                          </div>
                        ) : (
                          <p>{selectedOrder.address || selectedOrder.serviceAddress}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Timeline with Status Timestamps */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Clock className="h-5 w-5 mr-2" />
                      Order Timeline & Status History
                    </h3>
                    <div className="space-y-3">
                      {/* Helper function to safely format date */}
                      {(() => {
                        const formatDate = (dateValue: any) => {
                          if (!dateValue) return null;
                          try {
                            let date;
                            
                            // Handle Firebase Timestamp objects
                            if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
                              date = dateValue.toDate();
                            } 
                            // Handle Firestore Timestamp with seconds
                            else if (dateValue?.seconds) {
                              date = new Date(dateValue.seconds * 1000);
                            }
                            // Handle regular date string or number
                            else {
                              date = new Date(dateValue);
                            }
                            
                            if (isNaN(date.getTime())) return null;
                            
                            return date.toLocaleString('en-IN', {
                              dateStyle: 'medium',
                              timeStyle: 'short'
                            });
                          } catch (error) {
                            return null;
                          }
                        };

                        // Get provider service data
                        const providerServiceData = getProviderServiceData();
                        
                        // Timeline debug logs (commented to prevent spam)
                        // console.log('=== TIMELINE DEBUG ===');
                        // console.log('Provider service data:', providerServiceData);
                        
                        // Use provider service data timestamps if available, otherwise use order timestamps
                        const createdDate = formatDate(selectedOrder.createdAt);
                        const placedDate = formatDate(selectedOrder.placedAt);
                        const pendingDate = formatDate(selectedOrder.pendingAt);
                        
                        // For confirmed/active/completed, prefer provider's timestamps if available
                        // Use actual Firebase field names
                        const confirmedDate = formatDate(
                          providerServiceData?.acceptedAt || 
                          providerServiceData?.confirmedAt || 
                          selectedOrder.acceptedAt ||  // Firebase field
                          selectedOrder.confirmedAt ||
                          // If order is confirmed, active, or completed - it must have been confirmed
                          (['confirmed', 'active', 'completed'].includes(selectedOrder.status) && selectedOrder.updatedAt)
                        );
                        
                        const activeDate = formatDate(
                          providerServiceData?.startedAt || 
                          providerServiceData?.activeAt || 
                          selectedOrder.startedAt ||  // Firebase field
                          selectedOrder.serviceStartTime ||  // Firebase field (your screenshot)
                          selectedOrder.activeAt ||
                          // If order is active or completed - it must have been active
                          (['active', 'completed'].includes(selectedOrder.status) && selectedOrder.updatedAt)
                        );
                        
                        const completedDate = formatDate(
                          providerServiceData?.completedAt || 
                          selectedOrder.completedAt ||  // Firebase field (your screenshot)
                          selectedOrder.serviceCompletedTime ||  // Alternative field name
                          // Only show updatedAt if status is explicitly completed
                          (selectedOrder.status === 'completed' && selectedOrder.updatedAt)
                        );
                        
                        const cancelledDate = formatDate(
                          selectedOrder.cancelledAt ||
                          ((selectedOrder.status === 'cancelled' || 
                            selectedOrder.status === 'cancelledByUser' || 
                            selectedOrder.status === 'cancelledByProvider') && selectedOrder.updatedAt)
                        );
                        
                        const updatedDate = formatDate(selectedOrder.updatedAt);
                        
                        // Formatted dates log (commented)
                        // console.log('Formatted dates:', { createdDate, confirmedDate, activeDate, completedDate });

                        return (
                          <>
                            {/* Created */}
                            {createdDate && (
                              <div className="flex items-start gap-3 pb-3 border-b border-indigo-200">
                                <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">Order Created</p>
                                  <p className="text-sm text-gray-600">{createdDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Placed */}
                            {placedDate && (
                              <div className="flex items-start gap-3 pb-3 border-b border-indigo-200">
                                <div className="w-2 h-2 rounded-full bg-blue-600 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">Order Placed</p>
                                  <p className="text-sm text-gray-600">{placedDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Pending */}
                            {pendingDate && (
                              <div className="flex items-start gap-3 pb-3 border-b border-indigo-200">
                                <div className="w-2 h-2 rounded-full bg-yellow-500 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900">Marked as Pending</p>
                                  <p className="text-sm text-gray-600">{pendingDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Confirmed */}
                            {confirmedDate && (
                              <div className="flex items-start gap-3 pb-3 border-b border-indigo-200">
                                <div className="w-2 h-2 rounded-full bg-indigo-600 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-indigo-700">Order Confirmed</p>
                                  <p className="text-sm text-gray-600">{confirmedDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Active (Service Started) */}
                            {activeDate && (
                              <div className="flex items-start gap-3 pb-3 border-b border-purple-200">
                                <div className="w-2 h-2 rounded-full bg-purple-600 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-purple-700">Order Active</p>
                                  <p className="text-sm text-purple-600">{activeDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Completed */}
                            {completedDate && (
                              <div className="flex items-start gap-3 pb-3 border-b border-green-200">
                                <div className="w-2 h-2 rounded-full bg-green-600 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-green-700">Order Completed</p>
                                  <p className="text-sm text-green-600">{completedDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Cancelled */}
                            {cancelledDate && (
                              <div className="flex items-start gap-3 pb-3 border-b border-red-200">
                                <div className="w-2 h-2 rounded-full bg-red-600 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-red-700">
                                    Order Cancelled
                                    {selectedOrder.cancelledBy && (
                                      <span className="ml-2 text-xs bg-red-100 px-2 py-0.5 rounded">
                                        by {selectedOrder.cancelledBy === 'user' ? 'Customer' : 'Service Provider'}
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-sm text-red-600">{cancelledDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Last Updated */}
                            {updatedDate && selectedOrder.updatedAt !== selectedOrder.createdAt && (
                              <div className="flex items-start gap-3">
                                <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5"></div>
                                <div className="flex-1">
                                  <p className="font-medium text-gray-700">Last Updated</p>
                                  <p className="text-sm text-gray-600">{updatedDate}</p>
                                </div>
                              </div>
                            )}
                            
                            {/* Show message if no timestamps */}
                            {!createdDate && !placedDate && !pendingDate && !confirmedDate && !activeDate && !completedDate && !cancelledDate && (
                              <div className="text-center py-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <p className="text-gray-700 text-sm font-medium">⚠️ No timeline data available</p>
                                <p className="text-xs text-gray-600 mt-2">
                                  Order status: <span className="font-semibold text-indigo-600">{selectedOrder.status}</span>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Check browser console for debugging info
                                </p>
                                <p className="text-xs text-gray-400 mt-2 italic">
                                  Timestamps will appear when order status changes
                                </p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Action Buttons - Smart Status Flow */}
                  <div className="space-y-4 pt-6 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Change Order Status
                      </label>
                      
                      {/* Show appropriate buttons based on current status */}
                      <div className="space-y-3">
                        {/* For Placed orders - can move to Pending or Confirm */}
                        {selectedOrder.status === 'placed' && (
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={() => {
                                updateOrderStatus(selectedOrder.id, 'pending');
                                setSelectedOrder(null);
                              }}
                              disabled={actionLoading}
                              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-medium disabled:opacity-50 transition-colors text-sm"
                            >
                              Mark as Pending
                            </button>
                            <button
                              onClick={() => {
                                updateOrderStatus(selectedOrder.id, 'confirmed');
                                setSelectedOrder(null);
                              }}
                              disabled={actionLoading}
                              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 transition-colors text-sm"
                            >
                              <CheckCircle className="h-4 w-4 inline mr-1" />
                              Confirm Order
                            </button>
                          </div>
                        )}
                        
                        {/* For Pending orders - can Confirm */}
                        {selectedOrder.status === 'pending' && (
                          <button
                            onClick={() => {
                              updateOrderStatus(selectedOrder.id, 'confirmed');
                              setSelectedOrder(null);
                            }}
                            disabled={actionLoading}
                            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50 transition-colors text-sm"
                          >
                            <CheckCircle className="h-4 w-4 inline mr-1" />
                            Confirm Order
                          </button>
                        )}
                        
                        {/* For Confirmed orders - can mark Active */}
                        {selectedOrder.status === 'confirmed' && (
                          <button
                            onClick={() => {
                              updateOrderStatus(selectedOrder.id, 'active');
                              setSelectedOrder(null);
                            }}
                            disabled={actionLoading}
                            className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 transition-colors text-sm"
                          >
                            Mark as Active (Service Started)
                          </button>
                        )}
                        
                        {/* For Active orders - can mark Partial Completed or Complete */}
                        {selectedOrder.status === 'active' && (
                          <div className="space-y-2">
                            <button
                              onClick={() => {
                                updateOrderStatus(selectedOrder.id, 'partial-completed');
                                setSelectedOrder(null);
                              }}
                              disabled={actionLoading}
                              className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50 transition-colors text-sm"
                            >
                              ⚠️ Mark as Partial Completed (Requires Payment)
                            </button>
                            <button
                              onClick={() => {
                                updateOrderStatus(selectedOrder.id, 'completed');
                                setSelectedOrder(null);
                              }}
                              disabled={actionLoading}
                              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 transition-colors text-sm"
                            >
                              ✓ Mark as Completed
                            </button>
                          </div>
                        )}
                        
                        {/* For Partial Completed orders - can Complete after payment */}
                        {selectedOrder.status === 'partial-completed' && (
                          <button
                            onClick={() => {
                              updateOrderStatus(selectedOrder.id, 'completed');
                              setSelectedOrder(null);
                            }}
                            disabled={actionLoading}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 transition-colors text-sm"
                          >
                            ✓ Complete Service (Payment Received)
                          </button>
                        )}
                        
                        {/* Cancellation Options (only for non-completed/cancelled orders) */}
                        {!['completed', 'cancelled', 'cancelledByUser', 'cancelledByProvider'].includes(selectedOrder.status) && (
                          <div className="pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-600 mb-2">Cancellation Options:</p>
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                onClick={() => {
                                  if (confirm('Cancel order on behalf of user?')) {
                                    updateOrderStatus(selectedOrder.id, 'cancelledByUser');
                                    setSelectedOrder(null);
                                  }
                                }}
                                disabled={actionLoading}
                                className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50 transition-colors text-xs"
                              >
                                <XCircle className="h-3 w-3 inline mr-1" />
                                User Cancelled
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Cancel order on behalf of service provider?')) {
                                    updateOrderStatus(selectedOrder.id, 'cancelledByProvider');
                                    setSelectedOrder(null);
                                  }
                                }}
                                disabled={actionLoading}
                                className="px-3 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 font-medium disabled:opacity-50 transition-colors text-xs"
                              >
                                <XCircle className="h-3 w-3 inline mr-1" />
                                Provider Cancelled
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {/* For completed/cancelled orders - show status message */}
                        {['completed', 'cancelled', 'cancelledByUser', 'cancelledByProvider'].includes(selectedOrder.status) && (
                          <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <p className="text-sm text-gray-600">
                              {selectedOrder.status === 'completed' ? '✅ Order has been completed' : '❌ Order has been cancelled'}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">No further status changes available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
